import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import { useMemo, useState } from "react";
import { toast } from "sonner";

const products = [
  {
    id: "lily",
    name: "Single Lily Bouquet",
    price: 1000,
    colors: ["Pink", "White", "Yellow"],
    wraps: ["White", "Blue", "Gray", "Yellow"],
    image: "https://storage.stardeck.ai/images/52b6f931-cd15-43c0-9a55-fb459de6f16c.jpg",
  },
  {
    id: "roses",
    name: "Mixed Rose Bouquet",
    price: 3000,
    colors: ["Pink", "White", "Yellow", "Purple"],
    wraps: ["White", "Blue", "Gray", "Yellow", "Black"],
    image: "https://storage.stardeck.ai/images/951ae04a-e64b-4f2f-b3f7-d92cf7098bb1.jpg",
  },
] as const;

type Selection = {
  color?: string;
  wrap?: string;
};

export default function Order() {
  const [selections, setSelections] = useState<Record<string, Selection>>({});
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });

  const isReady = useMemo(
    () =>
      products.every((p) => selections[p.id]?.color && selections[p.id]?.wrap) &&
      !!customer.name &&
      !!customer.email,
    [selections, customer]
  );

  async function submit() {
    if (!isReady) return toast.error("Complete selections and contact details");
    const items = products.map((p) => ({
      id: p.id,
      name: p.name,
      price: p.price,
      color: selections[p.id]?.color ?? "",
      wrap: selections[p.id]?.wrap ?? "",
    }));
    const res = await fetch("/api/order", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ customer, items }),
    });
    if (res.ok) toast.success("Order submitted! We'll email you soon.");
    else toast.error("Failed to submit order");
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 space-y-10">
      <header className="space-y-2">
        <h1 className="text-4xl font-bold" style={{ fontFamily: "var(--font-display)" }}>
          Order Bouquets
        </h1>
        <p className="text-muted-foreground">
          Customize flower colors and wrapping to match your feelings.
        </p>
      </header>

      <div className="grid md:grid-cols-2 gap-8">
        {products.map((p) => (
          <Card key={p.id}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                {p.name}
                <span className="text-accent text-lg">{p.price.toLocaleString()} THB</span>
              </CardTitle>
              <CardDescription>Pick your colors, we’ll handle the magic.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              <img src={p.image} alt={p.name} className="w-full rounded-xl border border-border" />

              <div className="grid gap-2">
                <Label>Flower colors</Label>
                <RadioGroup
                  className="grid grid-cols-2 gap-2"
                  value={selections[p.id]?.color}
                  onValueChange={(v) =>
                    setSelections((s) => ({ ...s, [p.id]: { ...s[p.id], color: v } }))
                  }
                >
                  {p.colors.map((c) => (
                    <div key={c} className="flex items-center gap-2 rounded-lg border px-3 py-2">
                      <RadioGroupItem id={`${p.id}-color-${c}`} value={c} />
                      <Label htmlFor={`${p.id}-color-${c}`}>{c}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div className="grid gap-2">
                <Label>Wrapping paper</Label>
                <Select
                  value={selections[p.id]?.wrap}
                  onValueChange={(v) =>
                    setSelections((s) => ({ ...s, [p.id]: { ...s[p.id], wrap: v } }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose wrap" />
                  </SelectTrigger>
                  <SelectContent>
                    {p.wraps.map((w) => (
                      <SelectItem key={w} value={w}>
                        {w}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Contact Details</CardTitle>
          <CardDescription>We’ll confirm your order via email.</CardDescription>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-3 gap-4">
          <div className="grid gap-2">
            <Label htmlFor="cname">Full Name</Label>
            <Input
              id="cname"
              value={customer.name}
              onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cemail">Email</Label>
            <Input
              id="cemail"
              type="email"
              value={customer.email}
              onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cphone">Phone</Label>
            <Input
              id="cphone"
              value={customer.phone}
              onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
            />
          </div>
        </CardContent>
      </Card>

      <div className="pt-2">
        <Button
          className="quirky-btn"
          size="lg"
          variant={isReady ? "default" : "secondary"}
          onClick={submit}
        >
          {isReady ? "Submit Order" : "Complete Your Selections"}
        </Button>
      </div>
    </div>
  );
}
